/**
 * Publishes the caller's plan to the tree, once, for every gate in the app.
 *
 * One read (`useCurrentPlan` shares the Billing page's query key, and re-reads
 * only on a plan refusal or when the tab regains focus) feeding one context. Everything that packages a feature — the transport ceiling, a nav
 * entry, a settings page, an `<EntitlementGate>` — asks `usePlan()` /
 * `useEntitled()` rather than resolving a subscription of its own.
 *
 * It also publishes the LOAD PROFILE every surface's budget scales by
 * (`useLoadBudget`), from the plan's metadata and the subscription's own — so
 * "how much does this customer load" is resolved in the same one place as
 * "what did this customer buy".
 *
 * Mounted ABOVE `<TransportPolicyProvider>`, which now reads its ceiling from
 * this context instead of resolving the plan a second time. That ordering is
 * the point: the transport ceiling was the first plan-shaped gate in the app
 * and it had to grow a private provider because nothing published the plan. The
 * second one would have grown another.
 */
import * as React from "react";
import { PlanProvider } from "@authe/ui-core";
import { useCurrentPlan } from "./currentPlan";
import { usePlanWithHint } from "./planHint";
import { useRefreshPlanOnRefusal } from "./planRefusals";

export function DashboardPlan({ children }: { children: React.ReactNode }): React.ReactElement {
  // While the read is in flight, the last plan this user got (see `planHint`).
  const { plan, overrides, metadata } = usePlanWithHint(useCurrentPlan());
  // A plan refusal from any write re-reads the plan, so every gate below stops
  // offering what the API just refused — without a reload.
  useRefreshPlanOnRefusal();
  return (
    <PlanProvider plan={plan} overrides={overrides} subscriptionMetadata={metadata}>
      {children}
    </PlanProvider>
  );
}
